import React, { useState, useMemo } from 'react'
import './QuestionsTable.css'
import ReactMarkdown from 'react-markdown'
import * as XLSX from 'xlsx'

/**
 * QuestionsTable – cluster‑agnostic version
 *
 * Props
 *  - points:        Array of objects representing the Q&A rows.
 *  - onRowClick:    Callback when the user clicks a row.
 *  - markedPoints:  Optional array of point.id values that should float to the top.
 *
 * Removed compared with the original version:
 *  - clusters & clusterColors props
 *  - "Cluster" column and all colouring logic
 *  - cluster_id entry in the Excel export
 */
const validadationStatusMap = {
  no_references: { label: 'NO REF', color: 'red' },
  perfect_match: { label: 'PERFECT', color: 'green' },
  partial_match: { label: 'PARTIAL', color: 'yellow' },
  extra_references: { label: 'EXTRA', color: 'yellow' },
  wrong_references: { label: 'WRONG', color: 'red' }
}

const QuestionsTableSlim = ({ points, onRowClick, markedPoints = [] }) => {
  /* ------------------------------------------------------------------
   * TABLE STATE & HELPERS
   * ---------------------------------------------------------------- */
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' })
  const [expandedSources, setExpandedSources] = useState({})

  const handleSort = (columnKey) => {
    let direction = 'ascending'
    if (sortConfig.key === columnKey && sortConfig.direction === 'ascending') {
      direction = 'descending'
    }
    setSortConfig({ key: columnKey, direction })
  }

  const getSortArrow = (columnKey) => {
    if (sortConfig.key === columnKey) {
      return sortConfig.direction === 'ascending' ? '▲' : '▼'
    }
    return null
  }

  const sortedPoints = useMemo(() => {
    let data = markedPoints.length > 0 ? points.filter((p) => markedPoints.includes(p.id)) : [...points]

    if (sortConfig.key != null) {
      data.sort((a, b) => {
        const aVal = a[sortConfig.key]
        const bVal = b[sortConfig.key]
        if (aVal < bVal) return sortConfig.direction === 'ascending' ? -1 : 1
        if (aVal > bVal) return sortConfig.direction === 'ascending' ? 1 : -1
        return 0
      })
    }
    return data
  }, [points, markedPoints, sortConfig])

  // Compute pass-rate for each metric
  const totalPointsWithAR = sortedPoints.filter((p) => p.eval_data?.['Answer Relevancy']?.score !== undefined).length
  const totalPointsWithF = sortedPoints.filter((p) => p.eval_data?.['Faithfulness']?.score !== undefined).length

  const arSuccessPct = totalPointsWithAR
    ? Math.round(
        (sortedPoints.filter((p) => p.eval_data?.['Answer Relevancy']?.score >= 0.7).length / totalPointsWithAR) * 100
      )
    : 0
  const fSuccessPct = totalPointsWithF
    ? Math.round(
        (sortedPoints.filter((p) => p.eval_data?.['Faithfulness']?.score >= 0.7).length / totalPointsWithF) * 100
      )
    : 0

  /* ------------------------------------------------------------------
   * EXPORT
   * ---------------------------------------------------------------- */
  const exportToExcel = () => {
    const worksheetData = sortedPoints.map((p) => ({
      Question: p.query,
      Answer: p.answer,
      Tenant: p.tenant_id,
      Feedback: p.feedback === 1 ? 'Positive' : p.feedback === -1 ? 'Negative' : 'Neutral',
      Indexes: Array.from(new Set(p.used_indexes)).join(', '),
      Date: p.date,
      Session: p.session_id
    }))

    const ws = XLSX.utils.json_to_sheet(worksheetData)
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Questions and Feedback')
    XLSX.writeFile(wb, 'questions_and_feedback.xlsx')
  }

  /* ------------------------------------------------------------------
   * SMALL UTIL COMPONENTS
   * ---------------------------------------------------------------- */
  const TruncatedText = ({ text, maxLength }) => {
    const [isTruncated, setIsTruncated] = useState(true)

    const toggle = (e) => {
      e.stopPropagation()
      setIsTruncated((prev) => !prev)
    }

    const shortText = text.length > maxLength ? text.slice(0, maxLength) + '…' : text

    return (
      <span>
        {isTruncated ? <ReactMarkdown>{shortText}</ReactMarkdown> : <ReactMarkdown>{text}</ReactMarkdown>}
        {text.length > maxLength && (
          <button onClick={toggle} className="truncate-button">
            {isTruncated ? 'Read more' : 'Show less'}
          </button>
        )}
      </span>
    )
  }

  const toggleSources = (pointId, e) => {
    e.stopPropagation()
    setExpandedSources((prev) => ({ ...prev, [pointId]: !prev[pointId] }))
  }

  function formatSourceTitle(title) {
    if (title.startsWith('Manual-')) {
      title = title.replace('Manual-', '')
    }
    if (title.toLowerCase().endsWith('.md')) {
      title = title.slice(0, -3)
    }
    if (title && title[0] && title[0].toLowerCase() === title[0]) {
      title = title[0].toUpperCase() + title.slice(1)
    }
    return title
  }

  /* ------------------------------------------------------------------
   * RENDER
   * ---------------------------------------------------------------- */
  return (
    <div className="questions-table-container">
      <div className="table-header">
        <h2>Questions and Feedback</h2>
        <one-ux-button
          class="start-button"
          label="Export to Excel"
          purpose="default"
          weight="normal"
          state="default"
          onClick={exportToExcel}>
          <one-ux-icon icon="export" slot="start" set="default" />
        </one-ux-button>
      </div>

      <div className="table-wrapper">
        <table className="questions-table">
          <thead>
            <tr>
              <th onClick={() => handleSort('query')} className="sortable-header question-col">
                Question <span className="sort-arrow">{getSortArrow('query')}</span>
              </th>
              <th onClick={() => handleSort('answer')} className="sortable-header">
                Answer <span className="sort-arrow">{getSortArrow('answer')}</span>
              </th>
              <th onClick={() => handleSort('tenant_id')} className="sortable-header">
                Tenant <span className="sort-arrow">{getSortArrow('tenant_id')}</span>
              </th>
              <th onClick={() => handleSort('feedback')} className="sortable-header">
                Feedback <span className="sort-arrow">{getSortArrow('feedback')}</span>
              </th>
              <th>Indexes</th>
              <th onClick={() => handleSort('date')} className="sortable-header">
                Date <span className="sort-arrow">{getSortArrow('date')}</span>
              </th>
              <th onClick={() => handleSort('session_id')} className="sortable-header">
                Session <span className="sort-arrow">{getSortArrow('session_id')}</span>
              </th>
              <th
                className="metric-col"
                title={`Answer Relevancy: Measures how well the answer addresses the question. Success: ${arSuccessPct}%`}>
                AR
              </th>
              <th
                className="metric-col"
                title={`Faithfulness: Measures consistency with source information. Success: ${fSuccessPct}%`}>
                F
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedPoints.map((point) => {
              const evalData = point.eval_data || {}
              const ar = evalData['Answer Relevancy']
              const f = evalData['Faithfulness']
              return (
                <tr key={point.id} className="clickable-row" onClick={() => onRowClick(point)}>
                  <td className="question-col">{point.query}</td>
                  <td>
                    <TruncatedText text={point.answer} maxLength={40} />
                  </td>
                  <td>{point.tenant_id}</td>
                  <td>
                    <span
                      className={`feedback-tag ${
                        point.feedback === 1 ? 'positive' : point.feedback === -1 ? 'negative' : 'neutral'
                      }`}
                    />
                  </td>
                  <td className="sources-col">
                    {Array.from(new Set(point.used_indexes)).map((index, idx) => (
                      <div
                        key={idx}
                        className="index-item clickable-index"
                        onClick={(e) => toggleSources(point.id, e)}
                        style={{ cursor: 'pointer', marginBottom: '2px' }}>
                        {index}
                      </div>
                    ))}
                    {expandedSources[point.id] && point.selected_sources && point.selected_sources.length > 0 && (
                      <div className="sources-container" onClick={(e) => e.stopPropagation()}>
                        <div className="sources-header">
                          <strong>Selected Sources:</strong>
                          <one-ux-badge
                            set="colors"
                            label={validadationStatusMap[point.validation_status]?.label || point.validation_status}
                            text-color="white"
                            badge={validadationStatusMap[point.validation_status]?.color || 'grey'}
                            implicit={true}></one-ux-badge>
                        </div>
                        <ul className="sources-list">
                          {point.selected_sources.map((src, idx) => (
                            <li key={idx} className="source-item">
                              {formatSourceTitle(src)}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {expandedSources[point.id] &&
                      (!point.selected_sources || point.selected_sources.length === 0) &&
                      point.sources &&
                      point.sources.length > 0 && (
                        <div className="sources-container" onClick={(e) => e.stopPropagation()}>
                          <strong>Sources:</strong>
                          <ul className="sources-list">
                            {point.sources.map((source) => (
                              <li key={source.id} className="source-item">
                                {formatSourceTitle(source.title)} Chunk: {source.id}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                  </td>
                  <td>{point.date}</td>
                  <td>{point.session_id?.slice(-4)}</td>
                  <td className="metric-col">
                    <span
                      className={`metric-icon ${ar?.score > 0.7 ? 'pass' : 'fail'}`}
                      title={ar ? `${ar.score.toFixed(2)} – ${ar.reason}` : 'No eval data'}>
                      {ar ? (ar.score > 0.7 ? '✅' : '❌') : '-'}
                    </span>
                  </td>
                  <td className="metric-col">
                    <span
                      className={`metric-icon ${f?.score > 0.7 ? 'pass' : 'fail'}`}
                      title={f ? `${f.score.toFixed(2)} – ${f.reason}` : 'No eval data'}>
                      {f ? (f.score > 0.7 ? '✅' : '❌') : '-'}
                    </span>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default QuestionsTableSlim
